import React, { useEffect, useMemo, useState } from "react";
import SlideOutSidebar from "../../../../../ui-components/slide-out-sidebar/slideOutSidebar";
import Table from "ui-components/table/table";
import { createColumnHelper } from "@tanstack/react-table";
import { useCustomerViewPickupSummaryDetail } from "hooks/usePickupSummary";
import {
    CustomerViewPickupSummaryFilterListModel,
    CustomerViewPickupSummaryQueryParams,
    pickupFilterValueToQueryString,
} from "../../../../../app/data/pickup/models";
import { PickupStatus } from "app/data/common/route";
import { Routes } from "app/route/RoutesConfig";
import { useHistory } from "react-router-dom";

interface CustomerViewPickupDetailsProps {
    /** Requester company / team id (UUID). */
    requesterCompanyId: string;
    requesterCompany?: string;
    isOpen: boolean;
    onClose: () => void;
    filterData: CustomerViewPickupSummaryFilterListModel;
    data?: any[];
    initialStatus?: string;
    isLoading?: boolean;
}

const columnHelper = createColumnHelper<any>();

const customerDetailStatusMap: Record<string, PickupStatus> = {
    Created: PickupStatus.CREATED,
    Completed: PickupStatus.COMPLETED,
    Missed: PickupStatus.MISSED,
    Rescheduled: PickupStatus.RESCHEDULE_PICKUP,
    Canceled: PickupStatus.CANCELED,
};

const CustomerViewPickupDetails: React.FC<CustomerViewPickupDetailsProps> = ({
    requesterCompanyId,
    requesterCompany,
    isOpen,
    onClose,
    filterData,
    data,
    initialStatus,
    isLoading,
}) => {

    const history = useHistory();

    const tabs = ["Created", "Completed", "Missed", "Rescheduled", "Canceled"];
    const [selectedTab, setSelectedTab] = useState(initialStatus || "Created");

    const mergedFilter = useMemo((): CustomerViewPickupSummaryQueryParams => {
        const isCustom = filterData.timePeriod === "CUSTOM";
        const originTerminalNumbers =
            Array.isArray(filterData.requesterCompanyIds) && filterData.requesterCompanyIds.length > 0
                ? filterData.requesterCompanyIds.map((t) => t.value).join(",")
                : undefined;

        return {
            currentTerminalCode: filterData.currentTerminalCode,
            timePeriod: filterData.timePeriod,
            status: customerDetailStatusMap[selectedTab],
            from: isCustom ? pickupFilterValueToQueryString(filterData.from) : undefined,
            to: isCustom ? pickupFilterValueToQueryString(filterData.to) : undefined,
            originTerminalNumbers,
        };
    }, [filterData, selectedTab]);

    const {
        data: customerDetailListData,
        isLoading: listLoading,
        isFetchingNextPage: listFetchingNextPage,
        fetchNextPage,
        hasNextPage,
    } = useCustomerViewPickupSummaryDetail(requesterCompanyId, mergedFilter, {
        enabled: isOpen && !!requesterCompanyId,
    });

    const listRowsFromQuery = useMemo(
        () => customerDetailListData?.pages.flatMap((p) => p.data?.content ?? []) ?? [],
        [customerDetailListData]
    );

    const tableData = useMemo(() => {
    if (listRowsFromQuery.length > 0) return listRowsFromQuery;
    if ((listLoading || listFetchingNextPage) && data?.length) return data;
    return listRowsFromQuery;
}, [listRowsFromQuery, listLoading, listFetchingNextPage, data]);

    const columns = [
        columnHelper.accessor("formattedPickupNumber", {
            id: "formattedPickupNumber",
            header: "Pickup #",
            cell: (cellProps) => (
                <span
                    className="xgs-table-link-cell"
                    onClick={(e) => {
                        e.stopPropagation();
                        history.push(`${Routes.pickup.log}/${cellProps.getValue()}`)
                    }}>
                    {cellProps.getValue()}
                </span >
            ),
            size: 40,
        }),
        columnHelper.accessor("lastEventDate", {
            id: "lastEventDate",
            header: "Last Event Date",
            cell: (cellProps) => <>{cellProps.getValue().toUiDateTimeFormatLocal()}</>,
            size: 80,
        }),
        columnHelper.accessor("originTerminal", {
            id: "originTerminal",
            header: "Origin Terminal",
            size: 100,
        }),
        columnHelper.accessor("shipperName", {
            id: "shipperName",
            header: "Shipper",
            cell: (cellProps) => (
                <span>
                    {[
                        cellProps.getValue(),
                        cellProps?.row?.original?.shipperCity,
                        cellProps?.row?.original?.shipperState
                    ]
                        .filter(Boolean)
                        .join(", ")}
                </span>
            ),
            size: 80,
        }),
        columnHelper.accessor("consigneeName", {
            id: "consigneeName",
            header: "Consignee",
            cell: (cellProps) => (
                <span>
                    {[
                        cellProps.getValue(),
                        cellProps?.row?.original?.consigneePostalCode,
                    ]
                        .filter(Boolean)
                        .join(", ")}
                </span>
            ),
            size: 40,
        }),
        columnHelper.accessor("totalUnits", {
            id: "totalUnits",
            header: "Freight Details",
            cell: (cellProps) => (
                <span>
                    Units: {cellProps.getValue()}, lbs: {cellProps?.row?.original?.totalWeight}
                </span>
            ),
            size: 50,
        }),
        columnHelper.accessor("statusNotes", {
            id: "statusNotes",
            header: "Status Notes",
            size: 50,
        }),
    ];

    useEffect(() => {
        if (isOpen && initialStatus) {
            setSelectedTab(initialStatus);
        }
    }, [initialStatus, isOpen]);

    return (
        <SlideOutSidebar
            header={`${requesterCompany ?? ""} - Pickups`}
            onClose={onClose}
            show={isOpen}
            size="large"
        >
            <div className="tab-wrapper">
                <div className="tab-container">
                    {tabs.map((tab, index) => (
                        <div
                            key={tab}
                            onClick={() => setSelectedTab(tab)}
                            className={`tab-item ${selectedTab === tab ? 'active' : ''} ${index === 0 ? 'first' : index === tabs.length - 1 ? 'last' : ''
                                }`}
                        >
                            {tab}
                        </div>
                    ))}
                </div>
                <div>
                    <Table
                        isLoading={isLoading ?? (listLoading && !tableData.length)}
                        columns={columns}
                        data={tableData}
                        rowHeight={58}
                        minTableHeight={220}
                        noResultsText={`No Pickup Summary ${selectedTab} List`}
                        infiniteScroll
                        infiniteScrollHasNext={!!hasNextPage} 
                        infiniteScrollLoading={listFetchingNextPage}
                        onInfiniteScroll={() => fetchNextPage()}
                        responsive
                    />
                </div>
            </div>
        </SlideOutSidebar>
    );
};

export default CustomerViewPickupDetails;